import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, QueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { ConfigProvider } from 'antd';
import jaJP from 'antd/locale/ja_JP';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import './styles/responsive.css';

import AppLayout from './components/AppLayout';
import MobileGuard from './components/MobileGuard';
import ErrorFallback from './components/ErrorFallback';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PostListPage from './pages/PostListPage';
import PostDetailPage from './pages/PostDetailPage';
import CalendarPage from './pages/CalendarPage';
import ReportPage from './pages/ReportPage';
import ClientsPage from './pages/ClientsPage';

dayjs.locale('ja');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      retry: 1,
    },
  },
});

const theme = {
  token: {
    colorPrimary: '#1677ff',
    borderRadius: 6,
    fontFamily: "'Noto Sans JP', 'Emoji', -apple-system, BlinkMacSystemFont, sans-serif",
  },
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider locale={jaJP} theme={theme}>
        <QueryErrorResetBoundary>
          {({ reset }) => (
            <ErrorBoundary onReset={reset} FallbackComponent={ErrorFallback}>
              <BrowserRouter basename="/app">
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route element={<AppLayout />}>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/posts" element={<PostListPage />} />
                    <Route path="/posts/:id" element={<PostDetailPage />} />
                    <Route path="/calendar" element={<MobileGuard><CalendarPage /></MobileGuard>} />
                    <Route path="/reports" element={<MobileGuard><ReportPage /></MobileGuard>} />
                    <Route path="/clients" element={<MobileGuard><ClientsPage /></MobileGuard>} />
                  </Route>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </BrowserRouter>
            </ErrorBoundary>
          )}
        </QueryErrorResetBoundary>
      </ConfigProvider>
    </QueryClientProvider>
  );
};

export default App;
